import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { uploadChunks } from "@/lib/upload";
import { mediaUrl } from "@/components/home/ReelBanner";

const MAX = 80 * 1024 * 1024;

const Item = ({ it, i, count, onMove, onDelete }) => (
  <li data-testid={`banner-item-${i}`} className="w-44 shrink-0">
    <div className="aspect-[9/16] w-full overflow-hidden bg-[#1c1418]">
      {it.kind === "image"
        ? <img src={mediaUrl(it.url)} alt="" className="h-full w-full object-cover" />
        : <video src={mediaUrl(it.url)} muted playsInline preload="metadata" className="h-full w-full object-cover" />}
    </div>
    <div className="mt-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.15em]">
      <span>{String(i + 1).padStart(2, "0")} · {it.kind === "image" ? "Foto" : "Video"}</span>
      <span className="flex gap-1">
        <button disabled={i === 0} onClick={() => onMove(it, -1)} data-testid={`banner-left-${i}`} aria-label="Sposta prima" className="grid h-7 w-7 place-items-center border border-[#1c1418]/30 disabled:opacity-30"><ArrowLeft className="h-3.5 w-3.5" /></button>
        <button disabled={i === count - 1} onClick={() => onMove(it, 1)} data-testid={`banner-right-${i}`} aria-label="Sposta dopo" className="grid h-7 w-7 place-items-center border border-[#1c1418]/30 disabled:opacity-30"><ArrowRight className="h-3.5 w-3.5" /></button>
        <button onClick={() => onDelete(it)} data-testid={`banner-delete-${i}`} aria-label="Elimina" className="grid h-7 w-7 place-items-center bg-[#1c1418] text-white"><Trash2 className="h-3.5 w-3.5" /></button>
      </span>
    </div>
  </li>
);

export const BannerPanel = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const load = useCallback(() => {
    api.get("/site/banner").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Errore nel caricamento"));
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const [n, file] of files.entries()) {
      if (!/^(video|image)\//.test(file.type) || file.size > MAX) { toast.error(`${file.name}: solo foto o video fino a 80 MB`); continue; }
      const id = Math.random().toString(36).slice(2, 12);
      try {
        await uploadChunks("/admin/banner", file, (p) => setStatus(`File ${n + 1}/${files.length} · ${p}%`), `&upload_id=${id}`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    }
    setStatus("");
    load();
  };
  const move = async (it, d) => { await api.post(`/admin/banner/${it.id}/move?direction=${d}`); load(); };
  const remove = async (it) => {
    if (!window.confirm("Eliminare questo elemento dal banner?")) return;
    await api.delete(`/admin/banner/${it.id}`);
    load();
  };

  return (
    <div data-testid="banner-panel" className="space-y-8">
      <div className="max-w-xl space-y-3">
        <p className="font-display text-3xl uppercase leading-none">Banner homepage</p>
        <p className="text-sm">Foto e video che scorrono nel banner "On the reel" in homepage, nell'ordine qui sotto. Meglio verticali (9:16), video fino a 80 MB. Il banner appare appena c'è almeno un elemento.</p>
        <label data-testid="banner-upload" className="flex w-fit cursor-pointer items-center gap-2 bg-[#1c1418] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white">
          <Upload className="h-4 w-4 text-[#e9458f]" /> {status || "Carica foto o video"}
          <input type="file" multiple accept="video/*,image/*" onChange={upload} disabled={!!status} className="hidden" data-testid="banner-input" />
        </label>
      </div>
      {items.length ? (
        <ul className="flex gap-4 overflow-x-auto pb-2">
          {items.map((it, i) => <Item key={it.id} it={it} i={i} count={items.length} onMove={move} onDelete={remove} />)}
        </ul>
      ) : <p data-testid="banner-empty" className="font-mono text-xs uppercase tracking-[0.2em] text-[#1c1418]/70">Nessun elemento: il banner è nascosto.</p>}
    </div>
  );
};
